<template>
  <div :class="{ 'text-right': column.align === 'end' }">
    <div
      v-if="column.kind === 'account'"
      class="d-flex align-center account-cell"
    >
      <v-avatar v-if="pictureUrl" size="28" class="mr-2">
        <v-img :src="pictureUrl" :alt="displayValue" />
      </v-avatar>
      <span>{{ displayValue }}</span>
    </div>

    <rmr-soft-badge
      v-else-if="column.kind === 'network' && hasValue"
      :label="networkLabel"
      :icon="networkPresentation.icon"
      :color="networkPresentation.color"
      :background="networkPresentation.background"
    />

    <rmr-status-chip
      v-else-if="column.kind === 'status' && hasValue"
      :label="statusLabel"
      :variant="statusVariant"
      size="sm"
    />

    <template v-else-if="column.kind === 'verification'">
      <v-tooltip v-if="isVerifiedStatus" bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-icon color="#1877F2" v-bind="attrs" v-on="on">
            {{ icons.mdiCheckDecagram }}
          </v-icon>
        </template>
        <span>Compte vérifié</span>
      </v-tooltip>
      <span v-else-if="value === 'not_verified'">Non vérifié</span>
      <span v-else>{{ formattedValue }}</span>
    </template>

    <rmr-status-chip
      v-else-if="
        column.kind === 'boolean' && value !== null && value !== undefined
      "
      :label="value ? 'Oui' : 'Non'"
      :variant="value ? 'success' : 'neutral'"
      size="sm"
    />

    <a
      v-else-if="column.kind === 'post' && safePostLink"
      :href="safePostLink"
      target="_blank"
      rel="noopener noreferrer"
      class="post-title"
      :title="postDisplayValue"
    >
      {{ truncatedPostDisplayValue }}
    </a>

    <span v-else-if="column.kind === 'post'" class="post-title">
      {{ truncatedPostDisplayValue }}
    </span>

    <a
      v-else-if="column.kind === 'link' && safeLink"
      :href="safeLink"
      target="_blank"
      rel="noopener noreferrer"
    >
      Ouvrir
    </a>

    <v-tooltip v-else-if="column.kind === 'longText' && hasValue" bottom>
      <template v-slot:activator="{ on, attrs }">
        <span v-bind="attrs" class="long-text" v-on="on">
          {{ truncatedValue }}
        </span>
      </template>
      <span class="tooltip-text">{{ displayValue }}</span>
    </v-tooltip>

    <span v-else>{{ formattedValue }}</span>
  </div>
</template>

<script>
import {
  mdiAt,
  mdiButterflyOutline,
  mdiCheckDecagram,
  mdiFacebook,
  mdiInstagram,
  mdiLinkedin,
  mdiMastodon,
  mdiMusicNote,
  mdiPinterest,
  mdiSend,
  mdiSnapchat,
  mdiWeb,
  mdiYoutube,
} from "@mdi/js";
import RmrSoftBadge from "@/components/ui/feedback/RmrSoftBadge.vue";
import RmrStatusChip from "@/components/ui/feedback/RmrStatusChip.vue";
import { RMR_INDIGO, RMR_INDIGO_BG } from "@/styles/tokens";
import { formatDateTime } from "@/utils/formatting";
import { NETWORK_LABELS, STATUS_LABELS } from "./config";

const NETWORK_PRESENTATIONS = {
  bluesky: {
    icon: mdiButterflyOutline,
    color: "#087BF5",
    background: "#E8F3FF",
  },
  facebook: {
    icon: mdiFacebook,
    color: "#1877F2",
    background: "#EAF2FE",
  },
  instagram: {
    icon: mdiInstagram,
    color: "#C13584",
    background: "#FBEAF4",
  },
  linkedin: {
    icon: mdiLinkedin,
    color: "#0A66C2",
    background: "#E8F2FA",
  },
  mastodon: {
    icon: mdiMastodon,
    color: "#6364FF",
    background: "#EEEEFF",
  },
  pinterest: {
    icon: mdiPinterest,
    color: "#BD081C",
    background: "#FCEAEC",
  },
  snapchat: {
    icon: mdiSnapchat,
    color: "#8A7900",
    background: "#FFFBD6",
  },
  telegram: {
    icon: mdiSend,
    color: "#229ED9",
    background: "#E8F7FD",
  },
  threads: { icon: mdiAt, color: "#111111", background: "#F0F0F0" },
  tiktok: { icon: mdiMusicNote, color: "#111111", background: "#F0F0F0" },
  twitter: { icon: "", color: "#111111", background: "#F0F0F0" },
  x: { icon: "", color: "#111111", background: "#F0F0F0" },
  youtube: {
    icon: mdiYoutube,
    color: "#FF0000",
    background: "#FDECEC",
  },
};

export default {
  name: "SocialReportingCell",
  components: {
    RmrSoftBadge,
    RmrStatusChip,
  },
  data() {
    return {
      icons: { mdiCheckDecagram },
      RMR_INDIGO,
      RMR_INDIGO_BG,
    };
  },
  props: {
    column: {
      type: Object,
      required: true,
    },
    item: {
      type: Object,
      required: true,
    },
  },
  computed: {
    value() {
      return this.item[this.column.value];
    },
    hasValue() {
      return (
        this.value !== null && this.value !== undefined && this.value !== ""
      );
    },
    displayValue() {
      if (!this.hasValue) return "—";
      if (Array.isArray(this.value)) return this.value.join(", ");
      if (typeof this.value === "object") return JSON.stringify(this.value);
      return String(this.value);
    },
    truncatedValue() {
      return this.displayValue.length > 90
        ? `${this.displayValue.slice(0, 90)}…`
        : this.displayValue;
    },
    postDisplayValue() {
      return (
        this.item.title ||
        this.item.message ||
        this.item.description ||
        "Publication"
      );
    },
    truncatedPostDisplayValue() {
      return this.postDisplayValue.length > 90
        ? `${this.postDisplayValue.slice(0, 90)}…`
        : this.postDisplayValue;
    },
    networkLabel() {
      return NETWORK_LABELS[this.value] || this.displayValue;
    },
    networkPresentation() {
      return (
        NETWORK_PRESENTATIONS[String(this.value).toLowerCase()] || {
          icon: mdiWeb,
          color: this.RMR_INDIGO,
          background: this.RMR_INDIGO_BG,
        }
      );
    },
    statusLabel() {
      return (
        STATUS_LABELS[this.value] || this.item.status_text || this.displayValue
      );
    },
    statusVariant() {
      if (Number(this.value) === 200) return "success";
      if (Number(this.value) === 202) return "warning";
      return "error";
    },
    isVerifiedStatus() {
      const status = String(this.value || "").toLowerCase();
      return (
        status !== "not_verified" &&
        (status === "verified" || status.endsWith("_verified"))
      );
    },
    safeLink() {
      if (!this.hasValue) return null;
      try {
        const url = new URL(this.value);
        return ["http:", "https:"].includes(url.protocol) ? url.href : null;
      } catch (error) {
        return null;
      }
    },
    safePostLink() {
      if (!this.item.post_url) return null;
      try {
        const url = new URL(this.item.post_url);
        return ["http:", "https:"].includes(url.protocol) ? url.href : null;
      } catch (error) {
        return null;
      }
    },
    pictureUrl() {
      if (!this.item.picture_url) return null;
      try {
        const url = new URL(this.item.picture_url);
        return ["http:", "https:"].includes(url.protocol) ? url.href : null;
      } catch (error) {
        // Nonli peut renvoyer un chemin média relatif sans fournir sa base
        // publique. On conserve la valeur en base mais on n'affiche pas une
        // image cassée dans RMR.
        return null;
      }
    },
    formattedValue() {
      if (!this.hasValue) return "—";
      if (this.column.kind === "dateTime")
        return this.formatDateTime(this.value);
      if (this.column.kind === "month") return this.formatMonth(this.value);
      if (this.column.kind === "currency")
        return this.formatCurrency(this.value);
      if (this.column.kind === "percent") return this.formatPercent(this.value);
      if (this.column.kind === "number") return this.formatNumber(this.value);
      if (this.column.kind === "list") return this.displayValue;
      return this.displayValue;
    },
  },
  methods: {
    numberValue(value) {
      const number = Number(value);
      return Number.isFinite(number) ? number : null;
    },
    formatNumber(value) {
      const number = this.numberValue(value);
      if (number === null) return this.displayValue;
      return new Intl.NumberFormat("fr-FR", {
        maximumFractionDigits: this.column.decimals ?? 2,
      }).format(number);
    },
    formatCurrency(value) {
      const number = this.numberValue(value);
      if (number === null) return this.displayValue;
      return new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: this.item.currency || "EUR",
        maximumFractionDigits: this.column.decimals ?? 2,
      }).format(number);
    },
    formatPercent(value) {
      const number = this.numberValue(value);
      if (number === null) return this.displayValue;
      // Nonli renvoie déjà ces métriques dans l'unité pourcentage (78.9 = 78.9 %).
      return `${new Intl.NumberFormat("fr-FR", {
        maximumFractionDigits: 2,
      }).format(number)} %`;
    },
    formatDateTime(value) {
      // Nonli timestamps are stored as naive UTC in MariaDB. Displaying them
      // in the fixed business timezone keeps the visible day aligned with the
      // Paris-calendar bounds used by the API filters.
      return formatDateTime(value, "fr-FR", { timeZone: "Europe/Paris" });
    },
    formatMonth(value) {
      const match = String(value).match(/^(\d{4})-(\d{2})/);
      if (!match) return this.displayValue;
      return new Intl.DateTimeFormat("fr-FR", {
        month: "long",
        year: "numeric",
        timeZone: "UTC",
      }).format(new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1)));
    },
  },
};
</script>

<style scoped>
.account-cell {
  min-width: 160px;
}

.long-text {
  display: inline-block;
  min-width: 220px;
  max-width: 360px;
  white-space: normal;
}

.post-title {
  display: inline-block;
  min-width: 260px;
  max-width: 420px;
  white-space: normal;
}

.tooltip-text {
  display: inline-block;
  max-width: 520px;
  white-space: normal;
}
</style>
